import { Button, CircularProgress, SvgIcon } from '@civilgrid/ui/components';
import { bboxPolygon, booleanIntersects } from '@turf/turf';
import { isAxiosError } from 'axios';
import type { FeatureCollection, MultiPolygon, Polygon } from 'geojson';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { type MapRef, useMap } from 'react-map-gl/mapbox';
import { printMap as apiPrintMap } from '~/api/misc2';
import { notifyExportQueued } from '~/common/notifyExportQueued';
import { capturePosthogEvent, captureTypedPosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import EmailLink from '~/components2/EmailLink';
import Input from '~/components2/forms/Input';
import Radio from '~/components2/forms/Radio';
import { Notify } from '~/components2/Notify';
import { SendEmailModal } from '~/components2/SendEmailModal';
import layerStore from '~/store2/layers';
import { getLegendHeadings } from '../Legend/legendHeading';

// Keeps notes short enough for the map print PDF to fit them in its Notes box at a readable size.
const NOTES_MAX_LENGTH = 300;

interface PrintProps {
  projectPolygons: FeatureCollection<Polygon | MultiPolygon> | undefined;
}

const Print = ({ projectPolygons }: PrintProps) => {
  const throwPrintError = (data: any, err: any) => {
    const httpStatus = isAxiosError(err) ? err?.response?.status : undefined;
    let errorReason = '';
    if (httpStatus) {
      errorReason = String(httpStatus);
    } else if (err?.message) {
      errorReason = err.message;
    }
    captureTypedPosthogEvent(POSTHOG_EVENTS.mapExportFailed, {
      title: data.title,
      notes: data.notes,
      format: data.format,
      reason: errorReason,
    });
    Notify.error({
      title: 'Error Printing Map',
      message: 'There was an error while printing the map. Please try again or contact support.',
    });
  };

  const { current } = useMap();
  const [open, setOpen] = useState(false);
  const [imagePreview, setImagePreview] = useState('');

  function captureMapAsBase64(map: MapRef): Promise<string> {
    return new Promise((resolve) => {
      map.once('render', async () => {
        const canvas = new OffscreenCanvas(1280, 660);
        const mapCanvas = map.getCanvas();

        // crop the image to 1280 x 960 and "fill"
        const previewCanvasCtx = canvas.getContext('2d');

        const scaleFactor = Math.max(canvas.width / mapCanvas.width, canvas.height / mapCanvas.height);

        const newWidth = mapCanvas.width * scaleFactor;
        const newHeight = mapCanvas.height * scaleFactor;
        const x = canvas.width / 2 - newWidth / 2;
        const y = canvas.height / 2 - newHeight / 2;

        previewCanvasCtx!.drawImage(map.getCanvas(), x, y, newWidth, newHeight);

        const blob = await canvas.convertToBlob({ type: 'image/jpeg' });
        const fileReader = new FileReader();

        fileReader.addEventListener('load', (e) => {
          resolve(e.target!.result as string);
        });

        fileReader.readAsDataURL(blob);
      });

      map.setBearing(map.getBearing());
    });
  }

  const { getValues, control, watch } = useForm({
    defaultValues: {
      title: '',
      notes: '',
      format: 'jpeg',
    },
  });
  const printType = watch('format');

  const onSubmit = async (data: any, ccEmails: string[]) => {
    if (!current || !current.isStyleLoaded()) {
      throwPrintError(data, { message: 'Insufficient data.' });
      return false;
    }

    const bounds = current.getBounds();
    const style = current.getStyle();

    const { width, height } = current.getCanvas().style;
    const [widthNum, heightNum] = [width, height].map((str) => parseInt(str, 10));

    const basemap = style.sprite?.split('mapbox/')[1] || 'satellite-streets-v12';

    if (!bounds) {
      console.error(`Bounds not found for ${data.title}`);
      return false;
    }

    // Are there any projects on the map?
    const bboxPoly = bboxPolygon([bounds?.getWest(), bounds?.getSouth(), bounds?.getEast(), bounds?.getNorth()]);

    const polygonsWithinBBox = projectPolygons?.features.filter((poly) => booleanIntersects(poly, bboxPoly));

    // Prep project polygons for styling in the Print and Legend:
    polygonsWithinBBox?.forEach((poly) => {
      poly.properties = {
        ...poly.properties,
        style: {
          stroke: 'white',
          stokeWidth: '4',
          fill: 'rgba(255, 165, 0, 0.25)',
          layerName: 'Projects',
        },
      };
    });

    try {
      const { status } = await apiPrintMap(
        style.sources,
        data.title,
        data.notes,
        data.format,
        'A3',
        'landscape',
        imagePreview.split(',')[1], // remove base64 prefix
        polygonsWithinBBox,
        ccEmails,
        getLegendHeadings(Object.keys(style.sources), layerStore.tilesConfig),
      );

      if (status === 200) {
        notifyExportQueued({
          title: 'Print Map',
          message: `Your ${printType.toUpperCase()} download request is being processed. You will receive the file via email shortly.`,
          ccEmails,
        });

        capturePosthogEvent(POSTHOG_EVENTS.mapExported, {
          title: data.title,
          notes: data.notes,
          format: data.format,
        });

        return;
      }

      throwPrintError(data, { message: `Unexpected status: ${status}` });
      return false;
    } catch (err: any) {
      throwPrintError(data, err);
      return false;
    }
  };

  const loadImage = async (isOpen: boolean) => {
    if (isOpen && current) {
      const _loadImage = captureMapAsBase64(current);
      // this extra wait makes the UX better
      const loadingWait = new Promise((r) => setTimeout(r, 500));
      const [image] = await Promise.allSettled([_loadImage, loadingWait]);
      if (image.status === 'fulfilled') {
        setImagePreview(image.value);
      }
    } else {
      setImagePreview('');
    }
  };

  const changeOpen = (isOpen: boolean) => {
    setOpen(isOpen);
    loadImage(isOpen);
  };

  return (
    <div className="z-10">
      <Button
        variant="secondary"
        size="standard"
        className="w-7 h-7"
        aria-label="Print map"
        data-testid="map-print-open-button"
        onClick={() => changeOpen(true)}
      >
        <SvgIcon icon="print" size={14} />
      </Button>
      <SendEmailModal
        open={open}
        onOpenChange={changeOpen}
        title="Print Preview"
        testIdPrefix="map-print"
        submitLabel="Print"
        submitDisabled={imagePreview === ''}
        size="wide"
        onSend={(ccEmails) => onSubmit(getValues(), ccEmails)}
      >
        {imagePreview === '' ? (
          <div className="flex items-end justify-center">
            <CircularProgress indeterminate />
          </div>
        ) : (
          <div className="flex w-[60vw] flex-col gap-4">
            <img src={imagePreview} alt="Map print preview" className="rounded-lg border border-grey-30" />
            <Controller
              name="format"
              control={control}
              render={({ field: { onChange, ...rest } }) => (
                <Radio
                  items={[
                    {
                      label: 'JPEG',
                      value: 'jpeg',
                    },
                    { label: 'PDF', value: 'pdf' },
                  ]}
                  onChange={onChange}
                  className="flex-row flex-wrap items-end gap-4 p-1"
                  {...rest}
                />
              )}
            />

            {printType === 'pdf' && (
              <div className="flex flex-col gap-2">
                <Controller
                  name="title"
                  control={control}
                  render={({ field: { ref, ...rest } }) => <Input label="Title" {...rest} placeholder="Title" />}
                />
                <Controller
                  name="notes"
                  control={control}
                  render={({ field: { ref, ...rest } }) => (
                    <Input
                      label={`Notes (max ${NOTES_MAX_LENGTH} characters)`}
                      {...rest}
                      placeholder="Notes"
                      maxLength={NOTES_MAX_LENGTH}
                      data-testid="map-print-notes-input"
                    />
                  )}
                />

                <div className="text-2xs">
                  <span className="font-bold">NOTE: </span>The print output will be A3 Landscape format. Contact{' '}
                  <EmailLink>support@civilgrid.com</EmailLink> for additional sizes.
                </div>
              </div>
            )}
          </div>
        )}
      </SendEmailModal>
    </div>
  );
};

export default Print;
